import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  OFFER_PROBABILITY_LABELS,
  type OfferProbability,
} from "@/lib/offer-pipeline-calc";

const PROBABILITY_ORDER: OfferProbability[] = ["low", "likely", "very_likely"];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  offerTitle: string;
  /** Allerede satt på tilbudet — dialogen spør bare om det som mangler. */
  currentProbability: OfferProbability | null;
  currentExpectedCloseDate: string | null;
  isSubmitting?: boolean;
  onConfirm: (fields: { probability: OfferProbability; expectedCloseDate: string }) => void;
}

/**
 * VIB-2094 (Erik 28.09): rådgiver må sette sannsynlighet og estimert
 * close-dato før tilbudet sendes.
 *
 * Speiler LostReasonDialog: kalleren fanger opp statusendringen til «sendt»
 * FØR den skjer og åpner denne i stedet, og avbryt gir ingen statusendring.
 * Uten de to feltene er DL-widgeten «Tilbud i pipe» bare en liste med tall
 * uten tidshorisont, og det var nettopp tidshorisonten William ba om.
 *
 * Erik 28.09: de tre kategoriene beholdes, ikke fri prosent. Prosenten bak
 * hver kategori vises, så rådgiver ser hva valget betyr for det vektede
 * tallet.
 */
export function SendOfferDialog({
  open,
  onOpenChange,
  offerTitle,
  currentProbability,
  currentExpectedCloseDate,
  isSubmitting,
  onConfirm,
}: Props) {
  const [probability, setProbability] = useState<OfferProbability | null>(null);
  const [closeDate, setCloseDate] = useState("");

  // Forhåndsfyll med det som allerede står, så en rådgiver som har satt
  // sannsynlighet tidligere ikke må velge på nytt.
  useEffect(() => {
    if (!open) return;
    setProbability(currentProbability);
    setCloseDate(currentExpectedCloseDate ?? "");
  }, [open, currentProbability, currentExpectedCloseDate]);

  const canConfirm = !!probability && closeDate.length > 0 && !isSubmitting;

  function handleConfirm() {
    if (!probability || !closeDate) return;
    onConfirm({ probability, expectedCloseDate: closeDate });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Send «{offerTitle}»</DialogTitle>
          <DialogDescription>
            Før tilbudet sendes trenger vi et anslag på om det lander og når. Det er
            dette som gir daglig leder oversikt over pipen.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>Sannsynlighet for at det lander</Label>
            <div className="grid gap-1.5">
              {PROBABILITY_ORDER.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setProbability(p)}
                  aria-pressed={probability === p}
                  className={cn(
                    "rounded-md border px-3 py-2 text-left text-sm transition-colors",
                    probability === p
                      ? "border-primary bg-primary/10 font-medium"
                      : "border-border hover:bg-muted/50",
                  )}
                >
                  {OFFER_PROBABILITY_LABELS[p]}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="expected-close-date">Estimert close-dato</Label>
            <Input
              id="expected-close-date"
              type="date"
              value={closeDate}
              onChange={(e) => setCloseDate(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Når tror du kunden bestemmer seg? Dette er uavhengig av tilbudets
              gyldighet på 45 dager.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Avbryt
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}>
            {isSubmitting ? "Lagrer…" : "Send tilbud"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
